import React from 'react';

function formatCurrency(value) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value}`;
}

function formatNumber(value) {
  return value?.toLocaleString() || '—';
}

function LeadsPanel({
  leads,
  selectedLeads,
  selectedLead,
  onToggleSelection,
  onToggleSelectAll,
  onLeadClick,
  onDeleteLead,
  onRelookup,
  onExportCSV,
  onCopyClipboard,
  onPrintFlyers,
  onPrintDocxFlyers,
  onLookupDetails,
  onSaveToCRM,
  isSavingToCRM,
  parcelLoading,
  isLookingUp,
  lookupProgress,
  dailyLimit // { used, limit, remaining }
}) {
  const selectedCount = selectedLeads.size;
  const allSelected = leads.length > 0 && selectedCount === leads.length;

  // Count enriched leads
  const enrichedLeads = leads.filter(l => l.enriched);
  const parcelMatchLeads = leads.filter(l => l.parcelMatch);
  const hotLeads = leads.filter(l => l.leadQuality === 'hot');
  const ownerOccupiedLeads = leads.filter(l => l.ownerOccupied);
  const dbLeads = leads.filter(l => l.inDatabase);
  const needsLookup = leads.filter((_, i) => selectedLeads.has(i) && !leads[_]?.enriched).length;
  const selectedForLookup = leads.filter((l, i) => selectedLeads.has(i) && !l.enriched && !l.inDatabase).length;

  return (
    <aside className="leads-panel">
      <div className="panel-header">
        <div className="panel-icon">🏘️</div>
        <h2 className="panel-title">Your Leads</h2>
        <p className="panel-subtitle">Cross-checked with county records</p>
        {leads.length > 0 && (
          <div className="leads-count">
            <span>Found</span>
            <span className="leads-count-number">{leads.length}</span>
            <span>addresses</span>
          </div>
        )}
      </div>

      {leads.length > 0 && (
        <>
          <div className="stats-bar">
            <div className="stat-item">
              <div className="stat-value">{selectedCount}</div>
              <div className="stat-label">Selected</div>
            </div>
            <div className="stat-item">
              <div className="stat-value" style={{ color: '#ef4444' }}>{hotLeads.length}</div>
              <div className="stat-label">Hot</div>
            </div>
            <div className="stat-item">
              <div className="stat-value" style={{ color: '#22c55e' }}>{ownerOccupiedLeads.length}</div>
              <div className="stat-label">Owner</div>
            </div>
            <div className="stat-item">
              <div className="stat-value" style={{ color: '#8b5cf6' }}>{enrichedLeads.length}</div>
              <div className="stat-label" title="Detailed property data loaded">Enriched</div>
            </div>
            {dbLeads.length > 0 && (
              <div className="stat-item">
                <div className="stat-value" style={{ color: '#9ca3af' }}>{dbLeads.length}</div>
                <div className="stat-label" title="Already in database">In DB</div>
              </div>
            )}
          </div>

          {parcelLoading && (
            <div className="parcel-loading">
              <span className="spinner-small"></span>
              Loading county records...
            </div>
          )}

          <div className="select-all" onClick={onToggleSelectAll}>
            <div className={`lead-checkbox ${allSelected ? 'checked' : ''}`}></div>
            <span className="select-all-text">
              {allSelected ? 'Deselect All' : 'Select All'}
            </span>
          </div>
        </>
      )}

      <div className="leads-list">
        {leads.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📍</div>
            <h3 className="empty-title">No leads yet</h3>
            <p className="empty-text">
              Draw a circle, rectangle, or lasso on the map to find homeowner addresses.
            </p>
          </div>
        ) : (
          leads.map((lead, index) => (
            <div
              key={lead.placeId || index}
              className={`lead-item ${selectedLead?.placeId === lead.placeId ? 'highlighted' : ''} ${lead.leadQuality === 'hot' ? 'lead-hot' : ''} ${lead.inDatabase ? 'lead-in-db' : ''}`}
              onClick={() => onLeadClick(lead, index)}
            >
              <div
                className={`lead-checkbox ${selectedLeads.has(index) ? 'checked' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSelection(index);
                }}
              ></div>

              <div className="lead-info">
                <div className="lead-address-row">
                  <span className="lead-address">{lead.streetAddress}</span>
                  {lead.inDatabase && <span className="badge badge-db" title="Already in database">DB</span>}
                  {lead.enriched && <span className="badge badge-enriched" title="Detailed data loaded">✓</span>}
                  {lead.leadQuality === 'hot' && !lead.inDatabase && <span className="badge badge-hot">HOT</span>}
                  {lead.leadQuality === 'warm' && !lead.inDatabase && <span className="badge badge-warm">WARM</span>}
                  {lead.ownerOccupied && !lead.inDatabase && <span className="badge badge-occupied">OWNER</span>}
                </div>
                <div className="lead-city">
                  {[lead.city, lead.state, lead.zip].filter(Boolean).join(', ')}
                </div>

                {lead.enriched ? (
                  <div className="lead-parcel-data lead-enriched-data">
                    <div className="parcel-owner">
                      👤 {lead.ownerName || 'Unknown Owner'}
                    </div>
                    <div className="parcel-details">
                      <span title="Assessed Value">💰 {formatCurrency(lead.assessedValue)}</span>
                      <span title="Living Area">🏠 {formatNumber(lead.sqft)} sqft</span>
                      {lead.yearBuilt && <span title="Year Built">🏗️ {lead.yearBuilt}</span>}
                    </div>
                    <div className="parcel-roof-info">
                      <span title={lead.roofSqftMeasured ? "Satellite Measured" : "Calculated Estimate"}>
                        🔺 {formatNumber(lead.roofSqft || lead.estRoofSqft)} sqft roof
                        {lead.roofSqftMeasured && <span className="measured-badge" title="Satellite measured">📡</span>}
                      </span>
                      {lead.roofPitch && <span title="Roof Pitch">⛰️ {lead.roofPitch.toFixed(1)}°</span>}
                      {lead.roofAge && <span title="Roof Age">📅 {lead.roofAge}yr old</span>}
                      {lead.roofType && <span title="Roof Type">({lead.roofType})</span>}
                    </div>
                    <div className="parcel-estimate">
                      {lead.needsRoofReplacement && <span className="needs-replacement">⚠️ Needs Replacement </span>}
                      Est: {formatCurrency(lead.roofCostAsphalt || lead.estRoofCostLow)} - {formatCurrency(lead.roofCostMetal || lead.estRoofCostHigh)}
                    </div>
                    {lead.bedrooms && (
                      <div className="parcel-features">
                        {lead.bedrooms}bd/{lead.bathrooms}ba • {lead.floorCount && `${lead.floorCount} floors • `}Built {lead.yearBuilt}
                      </div>
                    )}
                  </div>
                ) : lead.parcelMatch || lead.inDatabase ? (
                  <div className="lead-parcel-data">
                    <div className="parcel-owner">
                      👤 {lead.ownerName?.split(',')[0] || 'Unknown'}
                    </div>
                    <div className="parcel-details">
                      <span title="Assessed Value">💰 {formatCurrency(lead.assessedValue)}</span>
                      <span title="Living Area">🏠 {formatNumber(lead.sqft)} sqft</span>
                      {lead.yearBuilt && <span title="Year Built">🏗️ {lead.yearBuilt}</span>}
                    </div>
                    {lead.estRoofSqft && (
                      <div className="parcel-roof">
                        <span title="Est. Roof Size">🔺 ~{formatNumber(lead.estRoofSqft)} sqft roof</span>
                        {lead.score && <span title="Lead Score">⭐ {lead.score}</span>}
                      </div>
                    )}
                    {(lead.estRoofCostLow || lead.estRoofCostHigh) && (
                      <div className="parcel-estimate">
                        Est. roof job: {formatCurrency(lead.estRoofCostLow)} - {formatCurrency(lead.estRoofCostHigh)}
                      </div>
                    )}
                    {lead.inDatabase && !lead.enriched && (
                      <div className="db-note">
                        <span>In CRM - click to view details</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="lead-no-match">
                    <span>Select &amp; click "Lookup Details" for owner info</span>
                  </div>
                )}
              </div>

              <div className="lead-actions">
                <button
                  className="copy-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    const addr = `${lead.streetAddress}, ${lead.city}, ${lead.state} ${lead.zip}`;
                    navigator.clipboard.writeText(addr);
                  }}
                  title="Copy address"
                >
                  📋
                </button>
                {lead.enriched && (
                  <button
                    className="relookup-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRelookup(index);
                    }}
                    title="Re-lookup (refresh data)"
                  >
                    🔄
                  </button>
                )}
                <button
                  className="delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteLead(index);
                  }}
                  title="Remove"
                >
                  ✕
                </button>
              </div>
              <div className="lead-number">{index + 1}</div>
            </div>
          ))
        )}
      </div>

      <div className="export-section">
        {/* Compact daily limit bar - hide for unlimited users */}
        {dailyLimit && !dailyLimit.unlimited && (
          <div className="daily-limit-compact">
            <span>{dailyLimit.remaining}/{dailyLimit.limit} lookups</span>
            <div className="daily-limit-bar-mini">
              <div className="daily-limit-fill" style={{ width: `${(dailyLimit.remaining / dailyLimit.limit) * 100}%` }}></div>
            </div>
          </div>
        )}

        {/* Primary action row */}
        <div className="action-row">
          {selectedForLookup > 0 && (
            <button
              className="action-btn lookup"
              onClick={onLookupDetails}
              disabled={isLookingUp || (dailyLimit && dailyLimit.remaining === 0)}
              title={`Enrich ${selectedForLookup} leads with owner data`}
            >
              {isLookingUp ? '...' : `🔍 Enrich (${selectedForLookup})`}
            </button>
          )}
          {selectedCount > 0 && (
            <button
              className="action-btn save"
              onClick={onSaveToCRM}
              disabled={isSavingToCRM}
            >
              {isSavingToCRM ? '...' : `💾 Save (${selectedCount})`}
            </button>
          )}
        </div>

        {isLookingUp && (
          <div className="lookup-progress-mini">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${lookupProgress ? (lookupProgress.current / lookupProgress.total) * 100 : 0}%` }}></div>
            </div>
            <span>{lookupProgress?.current || 0}/{lookupProgress?.total || 0}</span>
          </div>
        )}

        {/* Export row - always visible when leads exist */}
        {leads.length > 0 && (
          <div className="export-row">
            <button className="export-btn-mini" onClick={onExportCSV} disabled={selectedCount === 0} title="Export to CSV">
              📥 CSV
            </button>
            <button className="export-btn-mini" onClick={onCopyClipboard} disabled={selectedCount === 0} title="Copy addresses">
              📋
            </button>
            <button className="export-btn-mini accent" onClick={onPrintDocxFlyers} disabled={selectedCount === 0} title="Generate DOCX flyers">
              📄 Flyers
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

export default LeadsPanel;
